import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  Grid,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { SetRentDetailsDto } from '@/types/dto/rent-unit.dto';

import { useGetRentDetails } from '@/api/endpoints/rent/useGetRentDetails';
import { useGetRentPrices } from '@/api/endpoints/rent/useGetRentPrices';
import { useSetRentDetails } from '@/api/endpoints/rent/useSetRentDetails';

import FormTextField from '@/components/form/FormTextField';

interface RentDetailsFormValues {
  rentAmount: number;
  rentCurrency: string;
  rentInterval: 'month' | 'year';
}

interface RentDetailsFormSectionProps {
  unitId: number;
  onRentUpdate: (isNowDefined: boolean) => void;
}

const RentDetailsFormSection: React.FC<RentDetailsFormSectionProps> = ({
  unitId,
  onRentUpdate,
}) => {
  const theme = useTheme();
  const { data: currentRentDetails, isLoading: isLoadingRent } = useGetRentDetails(
    unitId,
    !!unitId
  );

  const { data: rentPrices = [], isLoading: isLoadingPrices } = useGetRentPrices(unitId, !!unitId);

  const rentDetailsForm = useForm<RentDetailsFormValues>({
    defaultValues: {
      rentAmount: 0,
      rentCurrency: 'usd',
      rentInterval: 'month',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (currentRentDetails) {
      const validInterval =
        currentRentDetails.interval === 'month' || currentRentDetails.interval === 'year'
          ? currentRentDetails.interval
          : 'month';

      rentDetailsForm.reset({
        rentAmount: currentRentDetails.amount,
        rentCurrency: currentRentDetails.currency,
        rentInterval: validInterval,
      });
    }
  }, [currentRentDetails, rentDetailsForm]);

  useEffect(() => {
    rentDetailsForm.register('rentAmount', {
      required: 'Rent amount is required',
      min: {
        value: 1,
        message: 'Rent must be greater than 0',
      },
    });
  }, [rentDetailsForm]);

  const setRentDetailsMutation = useSetRentDetails(unitId, {
    onSuccess: () => {
      onRentUpdate(true);
    },
  });

  const handleSetRent = rentDetailsForm.handleSubmit(data => {
    const rentData: SetRentDetailsDto = {
      amount: data.rentAmount * 100, // Convert back to cents
      currency: data.rentCurrency,
      interval: data.rentInterval,
    };
    setRentDetailsMutation.mutate(rentData);
  });

  // Format date string for display
  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  // Format currency for display
  const formatCurrency = (amount: number, isInCents = false): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    })
      .format(amount / (isInCents ? 100 : 1))
      .replace('$', '');

  const renderRentPriceHistory = () => {
    if (isLoadingPrices) {
      return (
        <Stack alignItems="center" justifyContent="center" height="100px">
          <CircularProgress />
        </Stack>
      );
    }
    if (rentPrices.length === 0) {
      return <Typography color="text.secondary">No price history available</Typography>;
    }
    return (
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 300,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: theme.palette.secondary[200],
                  textTransform: 'none',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  width: '25%',
                }}
              >
                Date Created
              </TableCell>
              <TableCell
                sx={{
                  bgcolor: theme.palette.secondary[200],
                  textTransform: 'none',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  width: '25%',
                }}
              >
                Amount
              </TableCell>
              <TableCell
                sx={{
                  bgcolor: theme.palette.secondary[200],
                  textTransform: 'none',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  width: '20%',
                }}
              >
                Currency
              </TableCell>
              <TableCell
                sx={{
                  bgcolor: theme.palette.secondary[200],
                  textTransform: 'none',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  width: '20%',
                }}
              >
                Interval
              </TableCell>
              <TableCell
                sx={{
                  bgcolor: theme.palette.secondary[200],
                  textTransform: 'none',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  width: '10%',
                  whiteSpace: 'nowrap',
                }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rentPrices.map(price => (
              <TableRow
                key={price.priceId}
                hover
                sx={{
                  '&:hover': {
                    backgroundColor: `${theme.palette.secondary[200]} !important`,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                  },
                }}
              >
                <TableCell>{formatDate(price.created)}</TableCell>
                <TableCell>${formatCurrency(price.amount)}</TableCell>
                <TableCell>{price.currency.toUpperCase()}</TableCell>
                <TableCell>Per {price.interval}</TableCell>
                <TableCell>
                  {price.active ? (
                    <Chip label="Current Rent" color="success" size="small" />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '16px',
                        display: 'inline-block',
                        padding: '2px 10px',
                        fontSize: '0.75rem',
                      }}
                    >
                      Inactive
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Card>
      <CardHeader
        title={currentRentDetails ? 'Update Monthly Rent' : 'Define Monthly Rent'}
        titleTypographyProps={{ variant: 'h6' }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
          In order to start collecting rent, you need to first define the rent amount and then
          generate a payment link.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Note: Once the link is generated, you'll need to send it to the tenant via email.
        </Typography>
        {isLoadingRent ? (
          <Stack alignItems="center" justifyContent="center" minHeight="100px">
            <CircularProgress />
          </Stack>
        ) : (
          <Box component="form" onSubmit={handleSetRent} noValidate sx={{ mt: 1 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                width: '100%',
              }}
            >
              <Grid container direction="column" spacing={2}>
                {/* Row 1: Rent Amount */}
                <Grid item>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={2}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Rent Amount
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FormTextField
                        name="rentAmount"
                        label=""
                        control={rentDetailsForm.control}
                        required
                        helperText={rentDetailsForm.formState.errors.rentAmount?.message}
                        error={!!rentDetailsForm.formState.errors.rentAmount}
                        textFieldProps={{
                          type: 'number',
                          InputProps: {
                            startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                          },
                          fullWidth: true,
                          size: 'small',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Row 2: Currency */}
                <Grid item>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={2}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Currency
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FormTextField
                        name="rentCurrency"
                        label=""
                        control={rentDetailsForm.control}
                        disabled
                        textFieldProps={{
                          fullWidth: true,
                          size: 'small',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Row 3: Interval */}
                <Grid item>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={2}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Interval
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FormTextField
                        name="rentInterval"
                        label=""
                        control={rentDetailsForm.control}
                        disabled
                        textFieldProps={{
                          fullWidth: true,
                          size: 'small',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Submit Button */}
                <Grid item>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={
                        setRentDetailsMutation.isPending ||
                        !!rentDetailsForm.formState.errors.rentAmount
                      }
                    >
                      {setRentDetailsMutation.isPending ? (
                        <CircularProgress size={24} />
                      ) : (
                        'Set Rent'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Rent Price History Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
            Rent Price History
          </Typography>

          {renderRentPriceHistory()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RentDetailsFormSection;
